/**
 * translateApiError — render a localized error message for an ApiError.
 *
 * i18n Phase 2 (2026-05-26).
 *
 * Cross-platform contract: backend emits
 *
 *     { "detail": { "code": "license.required",
 *                   "message": "<EN fallback>",
 *                   "<arg_key>": "<arg_value>", ... } }
 *
 * Frontend looks up the catalog via the dotted code:
 *
 *     t(`errors.${detail.code}`, detail)
 *
 * Fallback chain: localized string → backend message → axios message
 * → "Something went wrong."
 *
 * Use this helper everywhere we render an error to a user (toasts,
 * inline form errors, alert banners). Don't render `error.message`
 * directly — the helper picks up the i18n key when available so
 * future localization just works without per-call-site code change.
 *
 * Usage:
 *
 *   import { useTranslation } from 'react-i18next';
 *   import { translateApiError } from '../../i18n/translateApiError';
 *   import { ApiError } from '../../api/client';
 *
 *   const { t } = useTranslation();
 *   try {
 *     await api.foo();
 *   } catch (e) {
 *     if (e instanceof ApiError) {
 *       setError(translateApiError(e, t));
 *     }
 *   }
 */

import type { TFunction } from 'i18next';

import type { ApiError } from '../api/client';

interface StructuredDetail {
  code?: string;
  /** Some backends key the machine-readable code as `error` rather than `code`.
   *  `StepUpRequiredException` is the one that reaches a user this way. */
  error?: string;
  message?: string;
  [key: string]: unknown;
}

function isStructuredDetail(detail: unknown): detail is StructuredDetail {
  if (typeof detail !== 'object' || detail === null || Array.isArray(detail)) {
    return false;
  }
  const d = detail as StructuredDetail;
  return typeof d.code === 'string' || typeof d.error === 'string';
}

/**
 * Backend codes are the ONE naming. Both catalogs key off them verbatim under
 * `codes.*`, and `src/i18n/errorCodes.json` — generated from the backend source by
 * `scripts/extract_error_codes.py` — is the contract this repo tests itself against.
 *
 * WHY THIS REPLACED AN ALIAS MAP
 * ------------------------------
 * The catalogs used to be dotted camelCase (`license.required`) while the lookup key
 * is built from a snake_case code (`license_required`). Those two namings cannot
 * meet, so of 32 leaf keys exactly TWO were ever referenced and the other 30 —
 * 270 translated strings across nine locales — were unreachable. Every structured
 * error fell through to the backend's ENGLISH `message`.
 *
 * Aliasing each code to a catalog path would have been a patch per code, with the
 * mismatch left in place. Renaming the CODES was not an option: they are API surface
 * that a dozen frontend comparisons, svdy-mobile and possibly a tenant integration
 * read. So the catalog moved, the dead keys were deleted, and there is now nothing
 * to keep in sync by hand.
 */

/** Is this string something we must not show a user?
 *
 *  The LAST guard, and it covers two shapes measured rather than assumed:
 *
 *  1. A MACHINE CODE. `ApiError`'s constructor falls back to `detail.error` for its
 *     `message`, so a structured detail keyed `error` with no `message` put the raw
 *     code on screen — reported 2026-08-01 as a red alert reading
 *     `step_up_required`. Exactly TWO backend details are keyed that way
 *     (`step_up_required`, `over_worker_limit`); a first pass at this comment said
 *     four, which was wrong: `not_connected` and `unknown_provider` are keyed
 *     `code`, so they take shape 2 instead.
 *
 *  2. THE SYNTHESIZED FALLBACK. With nothing to say, `ApiError` invents
 *     "Request failed with status 409" and axios "…with status code 409".
 *     Developer text, unlocalized, shown to an SMB owner (svdy-rules §14).
 *
 *  Both take the generic localized line instead. A code is not a message in any
 *  language, and neither is an HTTP status. */
function isNotUserCopy(s: string): boolean {
  if (/^[a-z][a-z0-9]*(?:[_.][a-z0-9]+)*$/.test(s)) return true;
  return /^Request failed with status(?: code)? \d+$/.test(s);
}

/** The machine code of a structured backend error, or `null` for a plain one.
 *
 * Exported because a caller sometimes has to BRANCH on the code rather than just print it —
 * a refusal whose remedy is a different action (delete → archive) needs to offer that action,
 * and a red toast cannot. The two-level peel below is the same one `translateApiError` does;
 * duplicating it at a call site is how one of them drifts (svdy-rules §10.5).
 */
export function apiErrorCode(error: { detail?: unknown }): string | null {
  const raw = error.detail as { detail?: unknown } | unknown;
  const inner =
    typeof raw === 'object' && raw !== null && 'detail' in raw
      ? (raw as { detail?: unknown }).detail
      : raw;
  if (!isStructuredDetail(inner)) return null;
  return (inner.code ?? inner.error) ?? null;
}

export function translateApiError(
  error: ApiError,
  // Loose type — react-i18next's TFunction has heavy generics that
  // don't accept arbitrary string keys at compile time. Helper-level
  // signature matches the actual runtime API.
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  // axios sometimes wraps the body inside { detail: <body> }; FastAPI
  // ALWAYS does. So peel one level to find the structured payload.
  const raw = error.detail as { detail?: unknown } | unknown;
  const inner =
    typeof raw === 'object' && raw !== null && 'detail' in raw
      ? (raw as { detail?: unknown }).detail
      : raw;

  if (isStructuredDetail(inner)) {
    const code = (inner.code ?? inner.error)!;
    // NOT `errors.codes.${code}`. This repo's catalogs are PER-NAMESPACE files whose
    // top level is already inside the namespace (`errors.json` = `{ codes, internal }`),
    // and `ns: 'errors'` is passed separately — so a key carrying the namespace as its
    // first SEGMENT resolves `errors.errors.codes.<code>`, which exists nowhere.
    //
    // Measured 2026-08-27 against the real catalogs, in zh-CN:
    //   t('errors.codes.form_component_in_use', { ns: 'errors' })  ->  ''
    //   t('codes.form_component_in_use',        { ns: 'errors' })  ->  '此组件正被…'
    //
    // So EVERY structured error on this frontend rendered the backend's English
    // `message`, in all nine locales — the same defect the 2026-08-01 rename was
    // meant to fix, which moved the CATALOG and left the key construction wrong.
    // `errorCatalog.test.ts` compares key SETS and never renders, so it could not see
    // it; `structuredErrorRender.test.ts` now does.
    //
    // svdy-mobile builds the same key and is CORRECT there: it has one resource file
    // per locale under `{ translation: … }` whose top level does contain `errors`, and
    // it passes no `ns`. Same string, opposite verdict — which is why this had to be
    // measured per repo rather than reasoned about once.
    const key = `codes.${code}`;
    const localized = t(key, {
      ns: 'errors',
      defaultValue: '',
      ...inner,
    });
    if (localized && localized !== key && localized !== '') {
      return localized;
    }
    if (inner.message) return inner.message;
  }

  // Legacy string detail OR localization missing — return the backend
  // message verbatim. This is a graceful fallback during the per-module
  // migration phase. It is graceful only for a SENTENCE: a machine code is not a
  // message in any language, so those take the generic line instead.
  if (error.message && !isNotUserCopy(error.message)) return error.message;
  return t('internal.error', {
    ns: 'errors',
    defaultValue: 'Something went wrong. Please try again.',
  });
}

// Re-export TFunction type for callers that want to pass useTranslation()'s t
// directly without dealing with the loose adapter type.
export type { TFunction };
